import { useState } from "react";
import { Link } from "react-router-dom";

import { projects as projectsApi, questionLibrary } from "../api/resources";
import { RegistryTable } from "../components/RegistryTable";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  PageHeader,
  Progress,
  Stat,
  StatusBadge,
  Tabs,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { dateTime, percent } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { PipelineResult } from "../lib/types";

type Tab = "overview" | "retailers" | "warehouses" | "aliases" | "routing" | "carriers";

export function OnboardingPage() {
  const { organizationId, organization } = useCurrentOrganization();
  const [tab, setTab] = useState<Tab>("overview");
  const [pipeline, setPipeline] = useState<PipelineResult | undefined>();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();

  const projects = useAsync(
    () => (organizationId ? projectsApi.list(organizationId) : Promise.resolve([])),
    [organizationId],
  );
  const modules = useAsync(() => questionLibrary.modules(), []);

  if (!organizationId) {
    return (
      <Card>
        <EmptyState
          title="No organization selected"
          description="Create an organization to start an onboarding project."
          action={
            <Link className="btn btn--primary" to="/onboarding/new">
              Start discovery
            </Link>
          }
        />
      </Card>
    );
  }

  if (projects.loading) return <Loading />;

  const rows = projects.data ?? [];
  const active = rows.find((row) => row.status !== "complete") ?? rows[0];
  const moduleNames = new Map((modules.data ?? []).map((module) => [module.key, module.name]));

  async function runPipeline(projectId: string) {
    if (!organizationId) return;
    setBusy(true);
    setActionError(undefined);
    try {
      setPipeline(await projectsApi.runPipeline(organizationId, projectId));
      projects.reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function completeProject(projectId: string) {
    if (!organizationId) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await projectsApi.complete(organizationId, projectId);
      projects.reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Onboarding"
        description={`Discovery engagements for ${organization?.dba_name ?? organization?.legal_name ?? "this organization"}.`}
        actions={
          <>
            <Link className="btn" to="/onboarding/review">
              What we know
            </Link>
            <Link className="btn btn--primary" to="/onboarding/new">
              New onboarding project
            </Link>
          </>
        }
      />

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: "overview", label: "Overview" },
          { value: "retailers", label: "Retailers" },
          { value: "warehouses", label: "Warehouses" },
          { value: "aliases", label: "Warehouse aliases" },
          { value: "routing", label: "Order routing" },
          { value: "carriers", label: "Carriers" },
        ]}
      />

      {tab === "retailers" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="retailers"
          emptyTitle="No retailers or marketplaces yet"
          emptyDescription="These appear as the interview discovers them. You can also add and edit them directly here rather than answering the same questions again."
          columns={[
            { key: "name", label: "Retailer / marketplace" },
            { key: "relationship_type", label: "Relationship" },
            { key: "integration_method", label: "Orders via" },
            { key: "inventory_source", label: "Stock from", kind: "text" },
            { key: "shipment_tracking_method", label: "Tracking via", kind: "text" },
            { key: "returns_method", label: "Returns via", kind: "text" },
            { key: "packing_slip_required", label: "Packing slip" },
            { key: "is_active", label: "Active" },
            { key: "verification_status_code", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Name", required: true },
            {
              key: "channel_type",
              label: "Type",
              options: [
                "marketplace",
                "retailer_dropship",
                "retailer_wholesale",
                "dtc",
                "distributor",
                "other",
              ],
            },
            { key: "country", label: "Country" },
            { key: "order_source", label: "How their orders reach you" },
            { key: "inventory_source", label: "Which system feeds them stock" },
            {
              key: "integration_method",
              label: "Orders arrive by",
              options: ["rest_api", "edi", "sftp", "csv", "manual_portal", "email", "webhook"],
            },
            {
              key: "relationship_type",
              label: "Relationship",
              options: [
                "marketplace_seller",
                "dropship_vendor",
                "wholesale",
                "first_party_vendor",
                "direct_integration",
                "aggregator",
                "other",
              ],
            },
            { key: "party_role", label: "Are you the seller or the vendor?", options: ["seller", "vendor", "both"] },
            { key: "shipment_tracking_method", label: "How tracking reaches them" },
            { key: "cancellation_method", label: "How cancellations reach them" },
            { key: "returns_method", label: "How returns are handled" },
            { key: "invoice_method", label: "How you invoice them" },
            { key: "manual_portal_steps", label: "What has to be done by hand" },
            { key: "packing_slip_source", label: "Who produces the packing slip" },
          ]}
        />
      ) : null}

      {tab === "warehouses" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="warehouses"
          emptyTitle="No locations yet"
          emptyDescription="Warehouses discovered in the interview appear here. The operational detail — cutoffs, capabilities, safety stock — is maintained here rather than inside the survey."
          columns={[
            { key: "name", label: "Location" },
            { key: "alias", label: "Short code" },
            { key: "ownership", label: "Owned / 3PL" },
            { key: "wms_name", label: "Runs on", kind: "text" },
            { key: "cutoff_time", label: "Cutoff" },
            { key: "order_transmission_method", label: "Orders sent by", kind: "text" },
            { key: "shipment_confirmation_method", label: "Ship confirm by", kind: "text" },
            { key: "label_generation_method", label: "Labels by", kind: "text" },
            { key: "is_active", label: "Active" },
            { key: "verification_status_code", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Location name", required: true },
            { key: "alias", label: "Short code used in feeds and on labels" },
            {
              key: "ownership",
              label: "Owned or third party",
              options: ["owned", "third_party_3pl", "hybrid", "virtual"],
            },
            { key: "wms_name", label: "Which system they pick and pack in" },
            { key: "city", label: "City" },
            { key: "country", label: "Country" },
            { key: "timezone", label: "Timezone" },
            { key: "cutoff_time", label: "Same-day cutoff" },
            { key: "handling_time_days", label: "Handling time (days)" },
            { key: "safety_stock_policy", label: "Stock held back" },
            { key: "atp_logic", label: "How available stock is worked out" },
            { key: "order_transmission_method", label: "How orders reach the site" },
            { key: "shipment_confirmation_method", label: "How they confirm a shipment" },
            { key: "label_generation_method", label: "Who prints the label there" },
          ]}
        />
      ) : null}

      {tab === "routing" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="routing-policies"
          emptyTitle="No routing rules yet"
          emptyDescription="One rule per channel that differs from your default. A single-warehouse business does not need any."
          columns={[
            { key: "priority", label: "#" },
            { key: "name", label: "Rule" },
            { key: "priority_strategy", label: "Decided by" },
            { key: "routing_factors", label: "Factors" },
            { key: "fill_or_kill", label: "Must ship complete" },
            { key: "split_shipment_allowed", label: "Split allowed" },
            { key: "fallback_action", label: "If it cannot be filled" },
            { key: "verification_status_code", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "name", label: "Rule name", required: true },
            {
              key: "priority_strategy",
              label: "How the location is chosen",
              options: ["closest", "lowest_cost", "inventory", "customer", "retailer", "manual"],
            },
            { key: "inventory_buffer", label: "Stock held back from this channel" },
            { key: "hold_rules", label: "Which orders wait" },
            { key: "cancellation_rules", label: "Cancellation rules" },
            { key: "fallback_behavior", label: "What happens when routing fails" },
            { key: "priority", label: "Order this rule is applied in" },
            {
              key: "fallback_action",
              label: "If the preferred warehouse cannot fulfil it",
              options: [
                "try_next_warehouse",
                "split_order",
                "backorder",
                "hold_for_review",
                "cancel_line",
                "cancel_order",
                "request_transfer",
                "notify_retailer",
                "manual_decision",
              ],
            },
            { key: "service_level_requirement", label: "Service level required" },
            { key: "region", label: "Region this applies to" },
          ]}
        />
      ) : null}

      {tab === "aliases" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="warehouse-aliases"
          emptyTitle="No warehouse codes recorded"
          emptyDescription="A location is called one thing internally and something else by each retailer. Their name is what arrives on their documents, so a missing one means shipments that cannot be matched."
          columns={[
            { key: "alias_value", label: "Code" },
            { key: "alias_type", label: "Used by" },
            { key: "direction", label: "Direction" },
            { key: "is_canonical", label: "Canonical" },
            { key: "maintained_manually", label: "Maintained by hand" },
            { key: "verification_status_code", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "warehouse_id", label: "Warehouse ID", required: true },
            { key: "alias_value", label: "The code itself", required: true },
            {
              key: "alias_type",
              label: "Who uses this code",
              options: ["internal", "oms", "erp", "wms", "retailer", "third_party", "carrier"],
            },
            { key: "retailer_id", label: "Retailer ID (only for a retailer code)" },
            { key: "direction", label: "Sent, received, or both", options: ["outbound", "inbound", "both"] },
            { key: "source_note", label: "Where this code came from" },
          ]}
        />
      ) : null}

      {tab === "carriers" ? (
        <RegistryTable
          organizationId={organizationId}
          resource="carrier-accounts"
          emptyTitle="No carriers recorded"
          emptyDescription="Carriers appear as the interview names them. Whose account is billed is the part that decides who pays for a mis-ship."
          columns={[
            { key: "carrier", label: "Carrier" },
            { key: "mode", label: "Mode" },
            { key: "account_owner", label: "Account owner" },
            { key: "third_party_billing", label: "Third-party billed" },
            { key: "rate_source", label: "Rates from", kind: "text" },
            { key: "is_active", label: "Active" },
            { key: "verification_status_code", label: "Status", kind: "status" },
          ]}
          createFields={[
            { key: "carrier", label: "Carrier", required: true },
            { key: "account_label", label: "Which account (a nickname, never an account number)" },
            { key: "mode", label: "Mode", options: ["parcel", "ltl", "ftl", "courier"] },
            {
              key: "account_owner",
              label: "Whose account is billed",
              options: ["us", "retailer", "customer", "3pl", "third_party"],
            },
            { key: "rate_source", label: "Where rates come from" },
            { key: "restrictions", label: "Anything they will not carry" },
            { key: "dim_weight_handling", label: "How dimensional weight is handled" },
          ]}
        />
      ) : null}

      {tab !== "overview" ? null : (
      <div className="stack">
        <ErrorBanner error={projects.error ?? actionError} />

        {rows.length === 0 ? (
          <Card>
            <EmptyState
              title="No onboarding project yet"
              description="An onboarding project selects the modules to learn about and tracks what is still unknown."
              action={
                <Link className="btn btn--primary" to="/onboarding/new">
                  Start discovery
                </Link>
              }
            />
          </Card>
        ) : null}

        {active ? (
          <Card
            title={active.name}
            actions={
              <div className="row">
                <StatusBadge status={active.status} />
                <button disabled={busy} onClick={() => void runPipeline(active.id)}>
                  Run knowledge pipeline
                </button>
                <button
                  disabled={busy || active.status === "complete"}
                  onClick={() => void completeProject(active.id)}
                >
                  Mark complete
                </button>
              </div>
            }
          >
            <div style={{ maxWidth: 520, marginBottom: 16 }}>
              <Progress value={Number(active.completion_percentage)} />
            </div>

            <div className="grid grid--stats" style={{ marginBottom: 16 }}>
              {Object.entries(active.completion_detail?.components ?? {}).map(([name, detail]) => (
                <Stat
                  key={name}
                  label={name.replace(/_/g, " ")}
                  value={`${detail.done}/${detail.total}`}
                  hint={percent(detail.ratio * 100)}
                />
              ))}
            </div>

            <h3 style={{ marginBottom: 8 }}>Selected modules</h3>
            <div className="row">
              {active.selected_modules.map((key) => (
                <span key={key} className="badge badge--accent">
                  {moduleNames.get(key) ?? key}
                </span>
              ))}
            </div>

            <h3 style={{ margin: "18px 0 8px" }}>Next steps</h3>
            <ul className="checklist">
              <li>
                <span>1.</span>
                <span>
                  Complete the company questionnaire — <Link to="/interviews">interviews</Link>
                </span>
              </li>
              <li>
                <span>2.</span>
                <span>
                  Upload the product master, warehouse list and any SOPs —{" "}
                  <Link to="/uploads">uploads</Link>
                </span>
              </li>
              <li>
                <span>3.</span>
                <span>
                  Review proposed knowledge — <Link to="/knowledge">knowledge review</Link>
                </span>
              </li>
              <li>
                <span>4.</span>
                <span>
                  Resolve gaps and contradictions — <Link to="/gaps">gaps &amp; contradictions</Link>
                </span>
              </li>
            </ul>
          </Card>
        ) : null}

        {pipeline ? (
          <Card title="Last pipeline run">
            <div className="grid grid--stats">
              <Stat label="Facts created" value={String(pipeline.extraction.facts_created ?? 0)} />
              <Stat
                label="Facts corroborated"
                value={String(pipeline.extraction.facts_corroborated ?? 0)}
              />
              <Stat label="Contradictions found" value={pipeline.contradictions_detected} />
              <Stat label="Open gaps" value={pipeline.gaps_open} />
              <Stat label="Follow-ups raised" value={pipeline.follow_ups_created} />
              <Stat label="Completion" value={percent(pipeline.completion_percentage)} />
            </div>
          </Card>
        ) : null}

        {rows.length > 1 ? (
          <Card title="All projects" flush>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Status</th>
                    <th className="numeric">Modules</th>
                    <th style={{ width: 190 }}>Completion</th>
                    <th>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="numeric">{row.selected_modules.length}</td>
                      <td>
                        <Progress value={Number(row.completion_percentage)} />
                      </td>
                      <td className="small muted">{dateTime(row.started_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}
      </div>
      )}
    </>
  );
}
